// frontend/src/services/socketService.js
// Socket.io client service for real-time chat features

import { io } from "socket.io-client";
import { getApiBaseURL } from "../utils/api";

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();
    this.connectionPromise = null;
  }

  /**
   * Connect to the Socket.io server
   * @param {string} token - JWT auth token
   * @returns {Promise} Resolves when connected
   */
  connect(token) {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.isConnected && this.socket?.connected) {
      return Promise.resolve(this.socket);
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        const baseURL = getApiBaseURL();

        // Clean up existing socket if any
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.disconnect();
        }

        this.socket = io(baseURL, {
          auth: { token },
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          autoConnect: true,
        });

        // Connection successful
        this.socket.on("connect", () => {
          console.log("🔌 Socket connected:", this.socket.id);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.connectionPromise = null;
          resolve(this.socket);
        });

        // Connection error
        this.socket.on("connect_error", (error) => {
          console.error("🔌 Socket connection error:", error.message);
          this.reconnectAttempts++;

          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.connectionPromise = null;
            reject(new Error("Max reconnection attempts reached"));
          }
        });

        // Disconnected
        this.socket.on("disconnect", (reason) => {
          console.log("🔌 Socket disconnected:", reason);
          this.isConnected = false;

          // If server disconnected us, don't auto-reconnect
          if (reason === "io server disconnect") {
            this.socket.connect();
          }
        });

        // Reconnection events
        this.socket.on("reconnect", (attemptNumber) => {
          console.log("🔌 Socket reconnected after", attemptNumber, "attempts");
          this.isConnected = true;
          this.reconnectAttempts = 0;
        });

        this.socket.on("reconnect_failed", () => {
          console.error("🔌 Socket reconnection failed");
          this.isConnected = false;
          this.connectionPromise = null;
        });

        // Error handling
        this.socket.on("error", (error) => {
          console.error("🔌 Socket error:", error);
        });
      } catch (error) {
        console.error("🔌 Socket initialization error:", error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect from the server
   */
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.connectionPromise = null;
    this.listeners.clear();
    console.log("🔌 Socket disconnected manually");
  }

  /**
   * Check if connected
   */
  get connected() {
    return this.isConnected && this.socket?.connected;
  }

  /**
   * Get socket ID
   */
  get socketId() {
    return this.socket?.id;
  }

  // ==================== CONVERSATION EVENTS ====================

  /**
   * Join a conversation room
   */
  joinConversation(conversationId) {
    if (!this.connected || !conversationId) return;
    this.socket.emit("join:conversation", conversationId);
  }

  /**
   * Leave a conversation room
   */
  leaveConversation(conversationId) {
    if (!this.socket || !conversationId) return;
    this.socket.emit("leave:conversation", conversationId);
  }

  // ==================== TYPING EVENTS ====================

  /**
   * Emit typing started
   */
  startTyping(conversationId) {
    if (!this.connected || !conversationId) return;
    this.socket.emit("typing:start", conversationId);
  }

  /**
   * Emit typing stopped
   */
  stopTyping(conversationId) {
    if (!this.connected || !conversationId) return;
    this.socket.emit("typing:stop", conversationId);
  }

  // ==================== MESSAGE EVENTS ====================

  /**
   * Emit message sent
   */
  messageSent(conversationId, message) {
    if (!this.connected || !conversationId) return;
    this.socket.emit("message:sent", { conversationId, message });
  }

  /**
   * Emit message edited
   */
  messageEdited(conversationId, messageId, newContent) {
    if (!this.connected || !conversationId) return;
    this.socket.emit("message:edited", {
      conversationId,
      messageId,
      newContent,
      editedAt: new Date().toISOString(),
    });
  }

  /**
   * Emit message read
   */
  messageRead(conversationId, messageId) {
    if (!this.connected || !conversationId) return;
    this.socket.emit("message:read", { conversationId, messageId });
  }

  // ==================== PRESENCE EVENTS ====================

  /**
   * Request online status of specific users
   */
  requestPresence(userIds) {
    if (!this.connected || !Array.isArray(userIds)) return;
    this.socket.emit("presence:request", userIds);
  }

  /**
   * Send heartbeat to keep connection alive
   */
  sendHeartbeat() {
    if (!this.connected) return;
    this.socket.emit("heartbeat");
  }

  /**
   * Generic emit method
   * @param {string} event - Event name
   * @param {any} data - Data to send
   */
  emit(event, data) {
    if (!this.connected) {
      console.warn("Socket not connected, cannot emit", event);
      return;
    }
    this.socket.emit(event, data);
  }

  // ==================== EVENT LISTENERS ====================

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Event handler
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.socket) {
      console.warn("Socket not initialized, cannot subscribe to", event);
      return () => {};
    }

    this.socket.on(event, callback);

    // Track listener for cleanup
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from an event
   */
  off(event, callback) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
      this.listeners.get(event)?.delete(callback);
    } else {
      this.socket.off(event);
      this.listeners.delete(event);
    }
  }

  /**
   * Subscribe to an event once
   */
  once(event, callback) {
    if (!this.socket) return;
    this.socket.once(event, callback);
  }

  // ==================== CONVENIENCE METHODS ====================

  /**
   * Subscribe to new messages
   */
  onNewMessage(callback) {
    return this.on("message:new", callback);
  }

  /**
   * Subscribe to message updates (edits)
   */
  onMessageUpdated(callback) {
    return this.on("message:updated", callback);
  }

  /**
   * Subscribe to message notifications
   */
  onMessageNotification(callback) {
    return this.on("message:notification", callback);
  }

  /**
   * Subscribe to read receipts
   */
  onMessageRead(callback) {
    return this.on("message:read:ack", callback);
  }

  /**
   * Subscribe to typing updates
   */
  onTypingUpdate(callback) {
    return this.on("typing:update", callback);
  }

  /**
   * Subscribe to user going online
   */
  onUserOnline(callback) {
    return this.on("presence:online", callback);
  }

  /**
   * Subscribe to user going offline
   */
  onUserOffline(callback) {
    return this.on("presence:offline", callback);
  }

  /**
   * Subscribe to presence response
   */
  onPresenceResponse(callback) {
    return this.on("presence:response", callback);
  }

  /**
   * Subscribe to user joined conversation
   */
  onUserJoined(callback) {
    return this.on("user:joined", callback);
  }

  /**
   * Subscribe to user left conversation
   */
  onUserLeft(callback) {
    return this.on("user:left", callback);
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
